/* Cleanlynk instant search
   - debounced requests to Shopify's predictive search, rendered by sections/predictive-search.liquid
   - in-memory cache + request cancellation, so fast typing never shows stale results
   - keyboard navigation (↑ ↓ Enter Esc) with aria-activedescendant
   - recent searches stored on the device, popular searches from theme settings
   - full-screen overlay on mobile */
(() => {
  if (customElements.get('predictive-search')) return;

  const RECENT_KEY = 'cl-recent-searches';
  const MAX_RECENT = 5;
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const readRecent = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  };
  const saveRecent = (term) => {
    term = (term || '').trim();
    if (term.length < 2) return;
    const list = [term, ...readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, MAX_RECENT);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-search-panel]');
      this.empty = this.querySelector('[data-search-empty]');
      this.results = this.querySelector('[data-search-results]');
      this.loading = this.querySelector('[data-search-loading]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      this.activeIndex = -1;
      this.isOpen = false;
      this.enabled = !this.hasAttribute('data-disabled');

      this.onInput = this.debounce(() => this.search(), 140);
      this.input.addEventListener('input', () => { this.toggleClear(); this.onInput(); });
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.clearBtn.addEventListener('click', () => this.clear());
      this.querySelector('[data-search-close]').addEventListener('click', () => this.close());
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('submit', (e) => this.onQuickAdd(e), true);
      this.addEventListener('focusout', (e) => {
        if (!this.isOpen || this.classList.contains('is-mobile-open')) return;
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
      });

      document.addEventListener('click', (e) => { if (this.isOpen && !this.contains(e.target) && !e.target.closest('[data-search-open]')) this.close(); });
      document.querySelectorAll(`[data-search-open="${this.dataset.id}"]`).forEach((btn) => btn.addEventListener('click', () => this.openMobile()));
      this.toggleClear();
    }

    debounce(fn, wait) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    }

    get term() { return this.input.value.trim(); }

    toggleClear() { this.clearBtn.hidden = this.input.value.length === 0; }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.renderRecent();
      if (this.term) this.search(); else this.showEmpty();
      if (!this.backdrop && window.matchMedia('(min-width: 990px)').matches) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'search-backdrop';
        this.backdrop.setAttribute('aria-hidden', 'true');
        this.backdrop.addEventListener('click', () => this.close());
        document.body.appendChild(this.backdrop);
      }
    }

    openMobile() {
      // Phones (iOS especially) only raise the keyboard when focus() happens
      // synchronously inside the tap, so make the field visible and focus it
      // straight away rather than on the next frame.
      this.classList.add('is-mobile-open');
      const wrap = this.closest('.header__search');
      if (wrap) wrap.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      this.input.focus({ preventScroll: true });
      this.open();
      const len = this.input.value.length;
      try { this.input.setSelectionRange(len, len); } catch (e) {}
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.classList.remove('is-mobile-open');
      const wrap = this.closest('.header__search');
      if (wrap) wrap.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (this.backdrop) { this.backdrop.remove(); this.backdrop = null; }
    }

    clear() {
      this.input.value = '';
      this.toggleClear();
      this.showEmpty();
      this.input.focus();
    }

    showEmpty() {
      if (this.controller) this.controller.abort();
      this.loading.hidden = true;
      this.results.innerHTML = '';
      this.empty.hidden = false;
      this.renderRecent();
      this.activeIndex = -1;
    }

    renderRecent() {
      const wrap = this.querySelector('[data-recent-wrap]');
      const list = this.querySelector('[data-recent-list]');
      if (!wrap || !list) return;
      const recent = readRecent();
      wrap.hidden = recent.length === 0;
      list.innerHTML = recent.map((t) => `<a class="pill" href="${window.theme.routes.search}?q=${encodeURIComponent(t)}&options%5Bprefix%5D=last&options%5Bfields%5D=title%2Cproduct_type%2Cvendor%2Ctag%2Cbody%2Cauthor%2Cvariants.title%2Cvariants.sku%2Cvariants.barcode" data-search-term="${escapeHtml(t)}"><svg class="icon" style="width:14px;height:14px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>${escapeHtml(t)}</a>`).join('');
    }

    async search() {
      const term = this.term;
      if (!term) { this.showEmpty(); return; }
      if (!this.enabled) return;
      if (!this.isOpen) this.open();

      const key = term.toLowerCase();
      if (this.cache.has(key)) { this.render(this.cache.get(key), term); return; }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.loading.hidden = false;

      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,query,collection,page',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,tag,variants.title,variants.sku,variants.barcode'
      });

      try {
        const res = await fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const markup = html ? html.innerHTML : '';
        this.cache.set(key, markup);
        if (this.term.toLowerCase() === key) this.render(markup, term);
      } catch (err) {
        if (err.name !== 'AbortError') { this.loading.hidden = true; }
      }
    }

    render(markup, term) {
      this.loading.hidden = true;
      this.empty.hidden = true;
      this.results.innerHTML = markup;
      this.highlight(term);
      const nonePopular = this.results.querySelector('[data-search-none-popular]');
      if (nonePopular) {
        const pills = this.empty.querySelector('.pills:not([data-recent-list])');
        if (pills) nonePopular.innerHTML = `<div class="pills">${pills.innerHTML}</div>`;
      }
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    highlight(term) {
      const words = term.split(/\s+/).filter((w) => w.length > 1).map(escapeRegExp);
      if (!words.length) return;
      const re = new RegExp(`(${words.join('|')})`, 'gi');
      this.results.querySelectorAll('[data-highlight]').forEach((el) => {
        el.innerHTML = escapeHtml(el.textContent).replace(re, '<mark>$1</mark>');
      });
    }

    get options() {
      return Array.from(this.panel.querySelectorAll('[role="option"]')).filter((el) => el.offsetParent !== null);
    }

    onKeydown(e) {
      const opts = this.options;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!opts.length) return;
        e.preventDefault();
        if (!this.isOpen) this.open();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        this.setActive((this.activeIndex + dir + opts.length) % opts.length, opts);
      } else if (e.key === 'Enter' && this.activeIndex > -1 && opts[this.activeIndex]) {
        e.preventDefault();
        const target = opts[this.activeIndex];
        const link = target.matches('a') ? target : target.querySelector('a');
        if (link) { saveRecent(this.term); window.location.href = link.href; }
      } else if (e.key === 'Escape') {
        if (this.input.value) { this.clear(); } else { this.close(); this.input.blur(); }
      }
    }

    setActive(index, opts) {
      opts.forEach((o) => o.setAttribute('aria-selected', 'false'));
      this.activeIndex = index;
      const el = opts[index];
      el.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    onSubmit(e) {
      if (!this.term) { e.preventDefault(); return; }
      saveRecent(this.term);
    }

    onClick(e) {
      const termLink = e.target.closest('[data-search-term]');
      if (termLink) {
        e.preventDefault();
        this.input.value = termLink.dataset.searchTerm;
        this.toggleClear();
        this.search();
        this.input.focus();
        return;
      }
      const link = e.target.closest('.search-product a, .search-suggestion, [data-search-all]');
      if (link) saveRecent(this.term);
    }

    async onQuickAdd(e) {
      const form = e.target.closest('[data-quick-add]');
      if (!form || !window.theme.cart) return;
      e.preventDefault();
      const btn = form.querySelector('button');
      btn.classList.add('is-loading');
      try {
        await window.theme.cart.add(new FormData(form), { openDrawer: false });
        btn.textContent = '✓';
        window.theme.toast(window.theme.strings.added);
      } catch (err) {
        window.theme.toast(err.message || window.theme.strings.error);
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
